import { useDebouncedValue } from '@tanstack/react-pacer'
import { createFileRoute, Link, Outlet, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { OrganisationNavSelect } from '@/components/layout/organisation-nav-select'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { OperatorsTab } from '@/components/users/operators-tab'

export const Route = createFileRoute('/_app/users/$organisationId/operators')({
  head: () => ({ meta: [{ title: 'Users - MyJunea' }] }),
  component: OperatorsPage,
})

function OperatorsPage(): React.ReactElement {
  const { organisationId } = Route.useParams()
  const navigate = useNavigate()

  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch] = useDebouncedValue(searchQuery, { wait: 300 })

  const organisationIdNum = Number(organisationId)

  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        description="Manage system managers and operators."
        defaultFiltersOpen={true}
        action={
          <Button asChild>
            <Link
              to="/users/$organisationId/operators/new"
              params={{ organisationId }}
              resetScroll={false}
            >
              Add operator
            </Link>
          </Button>
        }
        filters={
          <>
            <OrganisationNavSelect
              currentOrganisationId={organisationIdNum}
              baseRoute="/users/$organisationId/operators"
            />
            <SearchInput
              placeholder="Search operators..."
              value={searchQuery}
              onValueChange={setSearchQuery}
              className="w-48"
            />
          </>
        }
      />

      <Tabs value="operators">
        <TabsList>
          <TabsTrigger value="users" asChild>
            <Link
              to="/users/$organisationId/system-managers"
              params={{ organisationId }}
              resetScroll={false}
            >
              System managers
            </Link>
          </TabsTrigger>
          <TabsTrigger value="operators" asChild>
            <Link
              to="/users/$organisationId/operators"
              params={{ organisationId }}
              resetScroll={false}
            >
              Operators
            </Link>
          </TabsTrigger>
        </TabsList>
        <p className="text-muted-foreground mt-3 max-w-3xl text-sm text-pretty">
          An operator is responsible for physically restocking and maintaining machines on location.
          Operators receive a notification when a machine needs to be restocked. An operator does
          not have access to the MyJunea portal, but is linked to specific machines.
        </p>

        <div className="mt-4">
          <OperatorsTab
            organisationId={organisationIdNum}
            search={debouncedSearch || undefined}
            onRowClick={(operator) =>
              void navigate({
                to: '/users/$organisationId/operators/$userId/edit',
                params: { organisationId, userId: String(operator.id) },
                resetScroll: false,
              })
            }
          />
        </div>
      </Tabs>

      <Outlet />
    </div>
  )
}
